import { useState, useCallback } from 'react';
import FlexService from '../../../services/FlexService';

/**
 * useFlexExecution - Hook for executing FLEX scripts
 *
 * Features:
 * - Execute scripts with optional trace
 * - Parse/validate scripts
 * - Manage execution state
 */
function useFlexExecution() {
  const [isRunning, setIsRunning] = useState(false);
  const [output, setOutput] = useState(null);
  const [diagnostics, setDiagnostics] = useState([]);
  const [traceEnabled, setTraceEnabled] = useState(false);

  // Execute a script
  const executeScript = useCallback(async (scriptBody, context = {}) => {
    setIsRunning(true);
    setOutput(null);

    try {
      const startTime = Date.now();
      const result = await FlexService.executeScript({
        scriptBody,
        context,
        trace: traceEnabled,
      });

      const processingTimeMs = Date.now() - startTime;

      console.log('Execution result:', result);
      console.log('Context/Variables:', result.context || result.variables);

      const outputData = {
        success: result.success !== false,
        result: result.result,
        variables: result.context || result.variables || {},  // Backend returns 'context'
        trace: result.trace,
        diagnostics: result.diagnostics || [],
        processingTimeMs: result.executionTimeMs || result.processingTimeMs || processingTimeMs,
      };

      console.log('Setting output:', outputData);
      setOutput(outputData);

      // Update diagnostics for editor markers
      setDiagnostics(result.diagnostics || []);

      return result;
    } catch (err) {
      console.error('Script execution error:', err);
      const errorOutput = {
        success: false,
        variables: {},
        diagnostics: [
          {
            severity: 'error',
            message: err.message || 'Execution failed',
            line: 1,
          },
        ],
        processingTimeMs: 0,
      };
      setOutput(errorOutput);
      setDiagnostics(errorOutput.diagnostics);
      // Don't throw - we've already set the error output
    } finally {
      setIsRunning(false);
    }
  }, [traceEnabled]);

  // Execute a saved script by ID
  const executeScriptById = useCallback(async (scriptId, context = {}) => {
    setIsRunning(true);
    setOutput(null);

    try {
      const startTime = Date.now();
      const result = await FlexService.executeScript({
        scriptId,
        context,
        trace: traceEnabled,
      });

      const processingTimeMs = Date.now() - startTime;

      setOutput({
        success: result.success !== false,
        result: result.result,
        variables: result.context || result.variables,  // Backend returns 'context'
        trace: result.trace,
        diagnostics: result.diagnostics || [],
        processingTimeMs: result.executionTimeMs || result.processingTimeMs || processingTimeMs,
      });

      setDiagnostics(result.diagnostics || []);
      return result;
    } catch (err) {
      console.error('Script execution error:', err);
      const errorOutput = {
        success: false,
        variables: {},
        diagnostics: [
          {
            severity: 'error',
            message: err.message || 'Execution failed',
            line: 1,
          },
        ],
        processingTimeMs: 0,
      };
      setOutput(errorOutput);
      setDiagnostics(errorOutput.diagnostics);
      // Don't throw - we've already set the error output
    } finally {
      setIsRunning(false);
    }
  }, [traceEnabled]);

  // Validate/parse script without executing
  const validateScript = useCallback(async (scriptBody) => {
    try {
      const result = await FlexService.parseScript(scriptBody);

      const newDiagnostics = result.diagnostics || [];
      setDiagnostics(newDiagnostics);

      return {
        valid: result.valid !== false && newDiagnostics.every((d) => d.severity !== 'error'),
        diagnostics: newDiagnostics,
      };
    } catch (err) {
      const errorDiagnostics = [
        {
          severity: 'error',
          message: err.message || 'Parse failed',
          line: 1,
        },
      ];
      setDiagnostics(errorDiagnostics);
      return {
        valid: false,
        diagnostics: errorDiagnostics,
      };
    }
  }, []);

  // Toggle trace mode
  const toggleTrace = useCallback(() => {
    setTraceEnabled((prev) => !prev);
  }, []);

  // Clear output
  const clearOutput = useCallback(() => {
    setOutput(null);
  }, []);

  // Clear diagnostics
  const clearDiagnostics = useCallback(() => {
    setDiagnostics([]);
  }, []);

  return {
    isRunning,
    output,
    diagnostics,
    traceEnabled,
    executeScript,
    executeScriptById,
    validateScript,
    toggleTrace,
    clearOutput,
    clearDiagnostics,
    setTraceEnabled,
  };
}

export default useFlexExecution;
